import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { MovieCard } from "@/components/MovieCard";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  Search,
  Film,
  ExternalLink,
  CheckCircle,
  XCircle,
  Loader2
} from "lucide-react";

export const AISearch = () => {
  const { isActive } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    if (!isActive) {
      toast.error("Votre compte doit être validé pour utiliser la recherche IA");
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      const response = await axios.post(`${API}/movies/ai-search`, { query });
      setResults(response.data);
    } catch (error) {
      toast.error("Erreur lors de la recherche IA");
      console.error("AI search error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="ai-search">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-violet-600 to-pink-600 mb-6">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mb-4">
              Recherche IA
            </h1>
            <p className="text-gray-400 max-w-lg mx-auto">
              Décrivez le type de film que vous recherchez et notre IA vous
              suggèrera des films correspondants.
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="mb-12">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 to-pink-600/20 rounded-2xl blur-xl" />
              <div className="relative glass rounded-2xl p-6">
                <div className="flex gap-4">
                  <div className="relative flex-1">
                    <Sparkles className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-violet-400" />
                    <Input
                      type="text"
                      placeholder="Ex: Films de science-fiction avec des voyages dans le temps..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="pl-12 py-6 text-lg bg-black/50 border-white/10 focus:border-violet-500 rounded-xl"
                      disabled={loading}
                      data-testid="ai-search-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 px-8 rounded-xl"
                    disabled={loading || !query.trim()}
                    data-testid="ai-search-submit"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Search className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </form>

          {/* Results */}
          {loading && (
            <div className="text-center py-16">
              <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-400">L'IA recherche des films...</p>
            </div>
          )}

          {results && !loading && (
            <div className="space-y-12 animate-fade-in">
              {/* Local Results */}
              {results.results?.length > 0 && (
                <div>
                  <h2 className="font-['Outfit'] text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Film className="w-5 h-5 text-violet-500" />
                    Dans votre bibliothèque ({results.results.length})
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {results.results.map((movie) => (
                      <MovieCard key={movie.movie_id} movie={movie} />
                    ))}
                  </div>
                </div>
              )}

              {/* AI Suggestions */}
              {results.ai_suggestions?.length > 0 && (
                <div>
                  <h2 className="font-['Outfit'] text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-pink-500" />
                    Suggestions de l'IA
                  </h2>
                  <div className="space-y-3">
                    {results.ai_suggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        className="glass rounded-xl p-4 flex items-start gap-4"
                        data-testid={`ai-suggestion-${idx}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-white font-semibold">
                              {suggestion.title}
                            </h3>
                            {suggestion.year && (
                              <span className="text-gray-500 text-sm">
                                ({suggestion.year})
                              </span>
                            )}
                            {suggestion.in_library ? (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Dans la bibliothèque
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                                <XCircle className="w-3 h-3 mr-1" />
                                Non disponible
                              </Badge>
                            )}
                          </div>
                          {suggestion.reason && (
                            <p className="text-gray-400 text-sm">
                              {suggestion.reason}
                            </p>
                          )}
                        </div>
                        {suggestion.in_library && suggestion.movie_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-violet-400 hover:text-violet-300"
                            onClick={() => navigate(`/movie/${suggestion.movie_id}`)}
                          >
                            Voir
                            <ExternalLink className="w-4 h-4 ml-1" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {results.results?.length === 0 && results.ai_suggestions?.length === 0 && (
                <div className="text-center py-12">
                  <Film className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">
                    Aucun résultat
                  </h3>
                  <p className="text-gray-500">
                    Essayez une autre recherche.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
